import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, PackageCheck, Plus, Copy, Wand2, X, Search } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_STATUSES = ["bozza", "da inviare", "inviato", "confermato", "in consegna", "ricevuto", "pagato"];
const emptyRow = () => ({ product_id: "", product_name: "", unit: "", quantity: "", unit_price: "", notes: "" });
const emptyForm = () => ({
  supplier_id: "", supplier_name: "",
  order_date: new Date().toISOString().slice(0, 10),
  expected_date: "",
  notes: "",
  items: [emptyRow()],
});

export default function Orders() {
  const { lists } = useLists();
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — action guards (permission-based, no role checks).
  const canWrite = can("orders.write");
  const canReceive = can("orders.receive");
  const STATUSES = lists.order_statuses?.length ? lists.order_statuses : DEFAULT_STATUSES;
  const [orders, setOrders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id, form}
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [productQuery, setProductQuery] = useState({});

  const load = async () => {
    // FASE 7.4 STEP G — tolerate individual 403 responses (a role may have
    // orders.read but lack inventory/suppliers/orders.write permissions).
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [o, sg, sp, inv] = await Promise.all([
      safe(api.get("/orders")),
      safe(api.get("/orders/suggestions")),
      safe(api.get("/suppliers")),
      safe(api.get("/inventory")),
    ]);
    setOrders(o.data); setSuggestions(sg.data); setSuppliers(sp.data); setInventory(inv.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setNewOpen(true); };
  const openEdit = (o) => {
    setEditing({ id: o.id, status: o.status });
    setForm({
      supplier_id: o.supplier_id || "",
      supplier_name: o.supplier_name || "",
      order_date: o.order_date || new Date().toISOString().slice(0, 10),
      expected_date: o.expected_date || "",
      notes: o.notes || "",
      items: (o.items || []).map((it) => ({
        product_id: it.product_id || "",
        product_name: it.product_name || "",
        unit: it.unit || "",
        quantity: it.quantity ?? "",
        unit_price: it.unit_price ?? "",
        notes: it.notes || "",
      })),
    });
    setNewOpen(true);
  };

  const updateRow = (i, patch) => setForm((f) => ({
    ...f, items: f.items.map((r, idx) => idx === i ? { ...r, ...patch } : r),
  }));
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, emptyRow()] }));
  const removeRow = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  // Product prefill: last known cost = inventory.last_purchase_price || unit_cost
  const selectProduct = (i, prodId) => {
    const p = inventory.find((x) => x.id === prodId);
    if (!p) return;
    const prefillPrice = p.last_purchase_price ?? p.unit_cost ?? "";
    updateRow(i, {
      product_id: p.id, product_name: p.name, unit: p.unit || "",
      unit_price: form.items[i].unit_price === "" ? prefillPrice : form.items[i].unit_price,
    });
  };

  // Filter products by supplier if supplier_id is set on inventory
  const productsForSupplier = useMemo(() => {
    if (!form.supplier_id) return inventory;
    const supplierProds = inventory.filter((p) => p.supplier_id === form.supplier_id);
    const others = inventory.filter((p) => p.supplier_id !== form.supplier_id);
    return [...supplierProds, ...others]; // supplier's first
  }, [inventory, form.supplier_id]);

  const filterProducts = (query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return productsForSupplier.slice(0, 20);
    return productsForSupplier.filter((p) => (p.name || "").toLowerCase().includes(q)).slice(0, 20);
  };

  const subtotal = form.items.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0), 0);

  const buildPayload = () => ({
    supplier_id: form.supplier_id,
    order_date: form.order_date,
    expected_date: form.expected_date || null,
    notes: form.notes?.trim() || null,
    status: "bozza",
    source: "manual",
    items: form.items
      .filter((r) => r.product_id || r.product_name)
      .map((r) => ({
        product_id: r.product_id || null,
        product_name: r.product_name || null,
        unit: r.unit || null,
        quantity: Number(r.quantity) || 0,
        unit_price: Number(r.unit_price) || 0,
        notes: r.notes || null,
      })),
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) { toast.error("Seleziona un fornitore"); return; }
    if (form.items.filter((r) => r.product_id || r.product_name).length === 0) {
      toast.error("Aggiungi almeno una riga"); return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editing) await api.put(`/orders/${editing.id}`, payload);
      else await api.post("/orders", payload);
      toast.success(editing ? "Ordine aggiornato" : "Ordine creato come bozza");
      setNewOpen(false); setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/orders/${id}`, { status }); toast.success("Stato aggiornato"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const receive = async (id) => {
    try { await api.post(`/orders/${id}/receive`); toast.success("Merce ricevuta, magazzino aggiornato"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore ricezione"); }
  };
  const duplicate = async (id) => {
    try { await api.post(`/orders/${id}/duplicate`); toast.success("Bozza creata dal duplicato"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Errore duplicazione"); }
  };
  const del = async (id, status) => {
    if (!window.confirm(`Eliminare l'ordine (stato: ${status})?`)) return;
    try { await api.delete(`/orders/${id}`); toast.success("Ordine eliminato"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Impossibile eliminare"); }
  };

  const createFromSuggestions = async () => {
    if (suggestions.length === 0) return;
    const bySupplier = {};
    for (const s of suggestions) {
      if (!s.supplier_id) continue;
      if (!bySupplier[s.supplier_id]) bySupplier[s.supplier_id] = [];
      bySupplier[s.supplier_id].push(s);
    }
    for (const [sid, items] of Object.entries(bySupplier)) {
      const sup = suppliers.find((x) => x.id === sid);
      try {
        await api.post("/orders", {
          supplier_id: sid,
          status: "bozza",
          source: "smart_reorder",
          order_date: new Date().toISOString().slice(0, 10),
          expected_date: new Date(Date.now() + (sup?.delivery_days || 2) * 86400000).toISOString().slice(0, 10),
          items: items.map((i) => ({
            product_id: i.product_id, product_name: i.product_name,
            quantity: i.suggested_qty, unit_price: i.unit_cost,
          })),
          notes: "Generato automaticamente da suggerimenti Smart Reorder",
        });
      } catch (err) {
        toast.error(`Fornitore ${sup?.name || sid}: ${err.response?.data?.detail || "errore"}`);
      }
    }
    toast.success("Ordini bozza creati");
    load();
  };

  return (
    <div data-testid="orders-page">
      <PageHeader eyebrow="Fornitori" title="Ordini"
        actions={
          <div className="flex gap-2 flex-wrap">
            {canWrite && (
              <Button data-testid="new-order-btn" onClick={openNew} className="bg-neutral-900 text-[#FDFBF7]">
                <Plus size={14} strokeWidth={1.5} /> Nuovo ordine
              </Button>
            )}
            {canWrite && (
              <Button data-testid="create-from-suggestions" variant="outline" onClick={createFromSuggestions} disabled={suggestions.length === 0}>
                <Wand2 size={14} strokeWidth={1.5} /> Crea ordini da suggerimenti
              </Button>
            )}
          </div>
        } />

      {suggestions.length > 0 && (
        <div className="border border-[#E2D9C8] rounded-lg p-6 bg-[#F3EDE1] mb-8" data-testid="suggestions-box">
          <div className="label-eyebrow mb-3">Proposta Smart Reorder</div>
          <ul className="space-y-1 text-sm">
            {suggestions.map((s) => (
              <li key={s.product_id} className="flex justify-between">
                <span>{s.product_name} <span className="text-neutral-500">({s.current}/{s.min_stock})</span></span>
                <span className="font-medium">{s.suggested_qty} · {fmtEUR(s.estimated_cost)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {orders.length === 0 && (
          <div className="text-sm text-neutral-500 border border-dashed border-[#E2D9C8] rounded-lg p-8 text-center">
            Nessun ordine. Clicca <b>Nuovo ordine</b> per crearne uno manualmente.
          </div>
        )}
        {orders.map((o) => {
          const isDraft = o.status === "bozza";
          const isFinal = o.status === "ricevuto" || o.status === "pagato";
          return (
          <div key={o.id} className="border border-[#E2D9C8] rounded-lg p-5 bg-white" data-testid={`order-${o.id}`}>
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-serif text-xl">{o.supplier_name}</div>
                  {o.source === "smart_reorder" && <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded-full">smart reorder</span>}
                  {o.source === "manual" && <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-700 rounded-full">manuale</span>}
                </div>
                <div className="text-xs text-neutral-500 mt-1">Ordine {o.order_date}{o.expected_date ? ` · Consegna prevista ${o.expected_date}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                {canWrite ? (
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className="w-40" data-testid={`status-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <span className="text-xs px-2 py-1 border border-[#E2D9C8] rounded" data-testid={`status-ro-${o.id}`}>{o.status}</span>
                )}
                {canWrite && isDraft && <Button size="sm" variant="outline" onClick={() => openEdit(o)} data-testid={`edit-${o.id}`}>Modifica</Button>}
                {canWrite && <Button size="sm" variant="outline" onClick={() => duplicate(o.id)} data-testid={`dup-${o.id}`}><Copy size={12} strokeWidth={1.5} /> Duplica</Button>}
                {canReceive && o.status !== "ricevuto" && o.status !== "pagato" && (
                  <Button size="sm" onClick={() => receive(o.id)} data-testid={`receive-${o.id}`} className="bg-emerald-800 hover:bg-emerald-900 text-[#FDFBF7]"><PackageCheck size={14} strokeWidth={1.5} /> Ricevi</Button>
                )}
                {canWrite && !isFinal && <button onClick={() => del(o.id, o.status)} className="text-neutral-400 hover:text-red-700" data-testid={`del-${o.id}`}><Trash2 size={14} strokeWidth={1.5} /></button>}
              </div>
            </div>
            <div className="overflow-x-auto -mx-5 md:mx-0 px-5 md:px-0">
            <table className="w-full text-sm mt-4 min-w-[560px] md:min-w-0">
              <thead><tr className="text-left label-eyebrow"><th className="py-2">Prodotto</th><th className="py-2 text-right">Qtà</th><th className="py-2">Unità</th><th className="py-2 text-right">Prezzo</th><th className="py-2 text-right">Totale</th></tr></thead>
              <tbody>
                {o.items?.map((it, i) => (
                  <tr key={i} className="border-t border-[#E2D9C8]">
                    <td className="py-2">{it.product_name}</td>
                    <td className="py-2 text-right">{it.quantity}</td>
                    <td className="py-2 text-neutral-500">{it.unit || "—"}</td>
                    <td className="py-2 text-right">{fmtEUR(it.unit_price)}</td>
                    <td className="py-2 text-right font-medium">{fmtEUR((it.line_total ?? it.quantity * it.unit_price))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {o.notes && <div className="mt-2 text-xs text-neutral-500">{o.notes}</div>}
            <div className="mt-3 text-right font-serif text-2xl">{fmtEUR(o.total)}</div>
          </div>
          );
        })}
      </div>

      {/* NEW / EDIT DIALOG */}
      <Dialog open={newOpen} onOpenChange={(v) => { setNewOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="new-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">{editing ? "Modifica bozza ordine" : "Nuovo ordine"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Fornitore *</Label>
                <Select value={form.supplier_id} onValueChange={(v) => {
                  const s = suppliers.find((x) => x.id === v);
                  setForm((f) => ({ ...f, supplier_id: v, supplier_name: s?.name || "" }));
                }}>
                  <SelectTrigger data-testid="new-order-supplier"><SelectValue placeholder="Seleziona fornitore..." /></SelectTrigger>
                  <SelectContent>
                    {suppliers.filter((s) => s.active !== false).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data ordine</Label>
                <Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} data-testid="new-order-date" />
              </div>
              <div>
                <Label>Consegna prevista</Label>
                <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} data-testid="new-order-expected" />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Prodotti *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="add-row-btn"><Plus size={12} /> Aggiungi riga</Button>
              </div>
              <div className="space-y-3">
                {form.items.map((row, i) => {
                  const options = filterProducts(productQuery[i]);
                  return (
                    <div key={i} className="border border-[#E2D9C8] rounded-lg p-3 space-y-2" data-testid={`row-${i}`}>
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-5">
                          <Label className="text-xs">Prodotto</Label>
                          <div className="relative">
                            <Search size={12} className="absolute left-2 top-3 text-neutral-400" />
                            <Input
                              placeholder="Cerca prodotto..."
                              value={row.product_name || productQuery[i] || ""}
                              onChange={(e) => { updateRow(i, { product_id: "", product_name: e.target.value }); setProductQuery({ ...productQuery, [i]: e.target.value }); }}
                              className="pl-7"
                              data-testid={`row-${i}-name`}
                            />
                            {(productQuery[i] || row.product_name === "") && options.length > 0 && !row.product_id && (
                              <div className="absolute z-10 bg-white border border-[#E2D9C8] rounded mt-1 w-full max-h-40 overflow-y-auto shadow">
                                {options.map((p) => (
                                  <button type="button" key={p.id} onClick={() => { selectProduct(i, p.id); setProductQuery({ ...productQuery, [i]: "" }); }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-[#F3EDE1]">
                                    {p.name} <span className="text-xs text-neutral-500">· {p.unit || ""} · attuale {fmtEUR(p.last_purchase_price ?? p.unit_cost ?? 0)}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Qtà</Label>
                          <Input type="number" min="0" step="0.01" value={row.quantity} onChange={(e) => updateRow(i, { quantity: e.target.value })} data-testid={`row-${i}-qty`} />
                        </div>
                        <div className="col-span-1">
                          <Label className="text-xs">Unità</Label>
                          <Input value={row.unit || ""} onChange={(e) => updateRow(i, { unit: e.target.value })} data-testid={`row-${i}-unit`} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Costo unit. (€)</Label>
                          <Input type="number" min="0" step="0.01" value={row.unit_price} onChange={(e) => updateRow(i, { unit_price: e.target.value })} placeholder="precompilato" data-testid={`row-${i}-price`} />
                        </div>
                        <div className="col-span-1 text-right pt-5 text-sm font-medium">
                          {fmtEUR((Number(row.quantity) || 0) * (Number(row.unit_price) || 0))}
                        </div>
                        <div className="col-span-1 pt-5">
                          <button type="button" onClick={() => removeRow(i)} className="text-neutral-400 hover:text-red-700" data-testid={`row-${i}-del`}><X size={14} /></button>
                        </div>
                      </div>
                      <Input placeholder="Note riga (opzionale)" value={row.notes || ""} onChange={(e) => updateRow(i, { notes: e.target.value })} className="text-xs" />
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-neutral-500 mt-2">Il costo unitario è precompilato dall'ultimo prezzo noto ma è modificabile — il valore salvato è quello confermato qui. Se un prodotto manca, crealo prima nel Magazzino.</p>
            </div>

            <div>
              <Label>Note ordine</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="new-order-notes" />
            </div>

            <div className="text-right font-serif text-2xl border-t border-[#E2D9C8] pt-3">
              Totale: {fmtEUR(subtotal)}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setNewOpen(false); setEditing(null); }}>Annulla</Button>
              <Button type="submit" disabled={saving} className="bg-neutral-900 text-[#FDFBF7]" data-testid="new-order-submit">
                {saving ? "Salvataggio…" : (editing ? "Salva modifiche" : "Crea bozza")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
